import { useEffect, useState } from 'react';
import { Monitor, Smartphone, Tablet } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button, Card, CardHeader, Field, Input, Pill, Spinner, StatusDot, useToast } from '@os/ui';
import { api, useApiMutation, useIdentities, useMe, useSessions } from '../api';
import { initials } from '../desktop/items';
import { Page, PageAlert } from '../desktop/Page';
import { describeUserAgent, type DeviceKind } from '../lib/userAgent';
import './account.css';

const PROVIDER_LABEL: Record<string, string> = { google: 'Google', microsoft: 'Microsoft', linkedin: 'LinkedIn' };
const DEVICE_ICON: Record<DeviceKind, typeof Monitor> = { desktop: Monitor, mobile: Smartphone, tablet: Tablet, unknown: Monitor };

function formatWhen(iso: string) {
  return new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

/** Account window: forms in the main column, identity / security summary / sessions in the sticky aside. */
export default function Account() {
  const { data, isPending, error } = useMe();
  const toast = useToast((s) => s.show);
  const [search, setSearch] = useSearchParams();
  const identities = useIdentities();
  const unlink = useApiMutation((provider: string) => api(`/me/identities/${provider}`, { method: 'DELETE' }), [['me', 'identities']]);
  // The issuer redirects back here after linking (`?linked=google`) or on failure (`?link_error=…`).
  useEffect(() => {
    const linked = search.get('linked'); const linkError = search.get('link_error');
    if (!linked && !linkError) return;
    if (linked) toast(`${PROVIDER_LABEL[linked] ?? linked} account linked`);
    if (linkError) toast(linkError, 'danger');
    setSearch({}, { replace: true });
  }, [search, setSearch, toast]);
  const [name, setName] = useState<string | null>(null);
  const [gh, setGh] = useState<string | null>(null);
  const [pw, setPw] = useState({ current: '', next: '' });
  const [mfa, setMfa] = useState<{ secret: string; otpauth_url: string } | null>(null);
  const [code, setCode] = useState('');
  const [disablePw, setDisablePw] = useState('');
  const sessions = useSessions();

  const rename = useApiMutation((display_name: string) => api('/me', { method: 'PATCH', body: { display_name } }), [['me']]);
  const setGithub = useApiMutation((github_login: string) => api('/me', { method: 'PATCH', body: { github_login } }), [['me']]);
  const changePw = useApiMutation((b: { current_password: string; new_password: string }) => api('/me/password', { method: 'POST', body: b }), [['me']]);
  const setup = useApiMutation(() => api('/me/mfa/setup', { method: 'POST' }), []);
  const enable = useApiMutation((c: string) => api('/me/mfa/enable', { method: 'POST', body: { code: c } }), [['me']]);
  const disable = useApiMutation((b: { code: string; password: string }) => api('/me/mfa/disable', { method: 'POST', body: { code: b.code, password: b.password } }), [['me']]);
  const revoke = useApiMutation((id: string) => api(`/me/sessions/${id}`, { method: 'DELETE' }), [['me', 'sessions']]);
  const err = (e: unknown) => toast(e instanceof Error ? e.message : 'Something went wrong', 'danger');

  if (isPending) return <Page title="Account"><div className="dk-fallback"><Spinner /></div></Page>;
  if (error || !data) return <Page title="Account"><PageAlert error={error ?? new Error('Could not load your account')} fallback="Could not load your account" /></Page>;
  const u = data.user;
  // "os-session" is the browser cookie session the issuer keeps; "os" is the launcher's own OIDC client.
  const appName = (id: string) => (id === 'os-session' ? 'Browser session' : id === 'os' ? 'OS' : data.apps.find((a) => a.id === id)?.name ?? id);
  const linkedCount = identities.data?.linked.length ?? 0;

  return (
    <Page title="Account" subtitle={u.email}>
      <div className="pg-grid-2">
        <div className="pg-col">
          <Card>
            <CardHeader title="Profile" />
            <div className="acct-body">
              <Field label="Display name"><Input value={name ?? u.display_name} onChange={(e) => setName(e.target.value)} /></Field>
              <div className="meta">Shown in every app and in the audit log. Email and organisation are managed by an administrator.</div>
              <div>
                <Button variant="primary" size="form" onClick={() => rename.mutate(name ?? u.display_name, { onSuccess: () => { toast('Saved'); setName(null); }, onError: err })} loading={rename.isPending} disabled={name === null || name === u.display_name || rename.isPending}>Save</Button>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="GitHub" />
            <div className="acct-body">
              <Field label="GitHub username" hint="When you create an app from Admin → Registered apps, this account is added to its repository.">
                <Input value={gh ?? u.github_login ?? ''} spellCheck={false} placeholder="your-github-login" className="mono" onChange={(e) => setGh(e.target.value.trim())} />
              </Field>
              <div className="meta">Leave empty to be matched by the public email on your GitHub profile instead (most profiles keep it private, so the username is the reliable way).</div>
              <div>
                <Button variant="primary" size="form" onClick={() => setGithub.mutate(gh ?? '', { onSuccess: () => { toast('Saved'); setGh(null); }, onError: err })} loading={setGithub.isPending} disabled={gh === null || gh === (u.github_login ?? '') || setGithub.isPending}>Save</Button>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title={u.has_password ? 'Password' : 'Set a password'} />
            <div className="acct-body">
              {!u.has_password && <div className="meta"><Pill tone="warn" small>No password</Pill> You currently sign in only through a linked account. Set a password to also sign in directly.</div>}
              <div className="acct-facts">
                {u.has_password && <Field label="Current password"><Input type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} /></Field>}
                <Field label="New password (min 10)"><Input type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} /></Field>
              </div>
              <div>
                <Button variant="primary" size="form" loading={changePw.isPending} disabled={(u.has_password && !pw.current) || pw.next.length < 10 || changePw.isPending} onClick={() => changePw.mutate({ current_password: pw.current, new_password: pw.next }, { onSuccess: () => { toast(u.has_password ? 'Password changed; other sessions signed out' : 'Password set'); setPw({ current: '', next: '' }); }, onError: err })}>{u.has_password ? 'Change password' : 'Set password'}</Button>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="Two-factor authentication" />
            <div className="acct-body">
              {u.mfa_enabled ? (
                <>
                  <div className="meta"><Pill tone="ok" small>Enabled</Pill> Authenticator app (TOTP). Disabling requires {u.has_password ? 'your password and ' : ''}a current code.</div>
                  <div className="acct-facts">
                    {u.has_password && <Field label="Password"><Input type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} /></Field>}
                    <Field label="Code"><Input inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} /></Field>
                  </div>
                  <div><Button variant="danger-outline" size="form" loading={disable.isPending} disabled={code.length !== 6 || (u.has_password && !disablePw) || disable.isPending} onClick={() => disable.mutate({ code, password: disablePw }, { onSuccess: () => { toast('MFA disabled'); setCode(''); setDisablePw(''); }, onError: err })}>Disable MFA</Button></div>
                </>
              ) : mfa ? (
                <>
                  <div className="meta">Add this secret to your authenticator app, then confirm with a code. Required in production.</div>
                  <div className="mono acct-secret">{mfa.secret}</div>
                  <a className="tl-link" href={mfa.otpauth_url}>Open in authenticator app (mobile)</a>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <Field label="6-digit code"><Input inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} /></Field>
                    <Button variant="primary" size="form" loading={enable.isPending} disabled={code.length !== 6 || enable.isPending} onClick={() => enable.mutate(code, { onSuccess: () => { toast('MFA enabled'); setMfa(null); setCode(''); }, onError: err })}>Confirm</Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="meta"><Pill tone="warn" small>Off</Pill> Protect every app with an authenticator code at sign-in.</div>
                  <div><Button variant="primary" size="form" loading={setup.isPending} disabled={setup.isPending} onClick={() => setup.mutate(undefined, { onSuccess: (r) => setMfa(r as { secret: string; otpauth_url: string }), onError: err })}>Set up MFA</Button></div>
                </>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Sign-in methods" />
            <div className="acct-body">
              {identities.data?.providers.length === 0 && <div className="meta">No social providers are configured on this OS. Sign in with your password.</div>}
              {identities.data && identities.data.providers.length > 0 && (
                <div className="acct-list">
                  {identities.data.providers.map((p) => {
                    const link = identities.data.linked.find((l) => l.provider === p.id);
                    return (
                      <div key={p.id} className="acct-row" data-kind="plain">
                        <div className="acct-main">
                          <div className="acct-title">{p.label}</div>
                          <div className="acct-meta">
                            {link ? <><Pill tone="ok" small>Linked</Pill><span>{link.email ?? 'Signed in with this provider'}</span></> : <><Pill small>Not linked</Pill><span>Use {p.label} to sign in</span></>}
                          </div>
                        </div>
                        {link
                          ? <Button size="row" variant="ghost" loading={unlink.isPending && unlink.variables === p.id} disabled={unlink.isPending} onClick={() => unlink.mutate(p.id, { onSuccess: () => toast(`${p.label} unlinked`), onError: err })}>Unlink</Button>
                          : <Button size="row" variant="secondary" onClick={() => { window.location.href = p.link_url; }}>Link</Button>}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="meta">Linked accounts sign you in without a password. The OS still issues every app token; providers never see your apps.</div>
            </div>
          </Card>
        </div>

        <aside className="pg-aside">
          <Card>
            <div className="acct-id">
              <span className="acct-avatar" aria-hidden>{initials(u.display_name)}</span>
              <div className="acct-main">
                <div className="acct-id-name truncate">{u.display_name}</div>
                <div className="meta truncate">{u.email}</div>
              </div>
            </div>
            <div className="acct-body" style={{ paddingTop: 0 }}>
              <div className="acct-facts">
                <div className="acct-fact"><span className="meta">Organisation</span><b className="truncate">{data.organisation.name}</b></div>
                <div className="acct-fact"><span className="meta">Member since</span><b>{new Date(u.created_at).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}</b></div>
              </div>
              <div className="acct-roles">
                <span className="meta">Roles</span>
                {Object.entries(u.roles).map(([app, rs]) => <Pill key={app} small tone={app === 'os' ? 'danger' : 'outline'}>{appName(app)}: {rs.join(', ')}</Pill>)}
                {Object.keys(u.roles).length === 0 && <span className="meta">None yet</span>}
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="Security" />
            <div className="acct-body">
              <div className="acct-checks">
                <div className="acct-check"><StatusDot tone={u.has_password ? 'ok' : 'warn'} /><span>Password</span><b>{u.has_password ? 'Set' : 'Not set'}</b></div>
                <div className="acct-check"><StatusDot tone={u.mfa_enabled ? 'ok' : 'warn'} /><span>Two-factor</span><b>{u.mfa_enabled ? 'Enabled' : 'Off'}</b></div>
                <div className="acct-check"><StatusDot tone={linkedCount > 0 ? 'ok' : 'off'} /><span>Linked accounts</span><b>{identities.data ? linkedCount : '…'}</b></div>
                <div className="acct-check"><StatusDot tone="off" /><span>Active sessions</span><b>{sessions.data?.length ?? '…'}</b></div>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="Active sessions" />
            <div className="acct-body">
              {sessions.isPending && <div className="meta">Loading sessions…</div>}
              <PageAlert error={sessions.error} fallback="Could not load sessions" />
              {sessions.data && sessions.data.length === 0 && <div className="meta">No active sessions.</div>}
              {sessions.data && sessions.data.length > 0 && (
                <div className="acct-list">
                  {sessions.data.map((s) => {
                    const device = describeUserAgent(s.user_agent);
                    const Icon = DEVICE_ICON[device.device];
                    return (
                      <div key={s.id} className="acct-row">
                        <span className="acct-device" aria-hidden><Icon size={16} strokeWidth={1.6} /></span>
                        <div className="acct-main">
                          <div className="acct-title truncate">{device.label}</div>
                          <div className="acct-meta">
                            <Pill small>{appName(s.client_id)}</Pill>
                            {s.ip && <span className="acct-dot">{s.ip}</span>}
                            <span className="acct-dot">{formatWhen(s.created_at)}</span>
                          </div>
                        </div>
                        <Button size="row" variant="ghost" loading={revoke.isPending && revoke.variables === s.id} disabled={revoke.isPending} onClick={() => revoke.mutate(s.id, { onError: err })}>Revoke</Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        </aside>
      </div>
    </Page>
  );
}
